import React, {
  useState,
  useMemo,
  useEffect,
  useRef,
  useCallback,
} from "react";
import Select from "react-select";
import Swal from "sweetalert2";
import "./AlertFormEdit.css";
import { useIntl } from "react-intl";
import { Toast } from "../../../helpers/alert";
import useFilteredUsersByDevice from "../../../hooks/useFilteredUsersByDevice";

const ThresholdAlertFormEdit = ({
  devices,
  users,
  onCancel,
  onSave,
  initialData,
  userPrivilege,
}) => {
  const intl = useIntl();

  // ---------- Normalizadores (IDs o strings → {value,label}) ----------
  // Usan "users" completos para rehidratar initialData de forma robusta
  const toEmailOptions = useCallback(
    (arr = []) => {
      const byId = new Map((users || []).map((u) => [Number(u.id), u]));
      const byEmail = new Map(
        (users || []).map((u) => [String(u.email || ""), u])
      );
      return arr
        .map((val) => {
          const str = String(val ?? "");
          const isNumeric = typeof val === "number" || /^[0-9]+$/.test(str);
          if (isNumeric) {
            const u = byId.get(Number(val));
            return u?.email ? { value: u.id, label: u.email } : null;
          }
          const u = byEmail.get(str);
          return u
            ? { value: u.id, label: u.email }
            : str
            ? { value: str, label: str }
            : null; // fallback string
        })
        .filter(Boolean);
    },
    [users]
  );

  const toPhoneOptions = useCallback(
    (arr = []) => {
      const byId = new Map((users || []).map((u) => [Number(u.id), u]));
      const byPhone = new Map(
        (users || []).map((u) => [String(u.phone || ""), u])
      );
      return arr
        .map((val) => {
          const str = String(val ?? "");
          const isNumeric = typeof val === "number" || /^[0-9]+$/.test(str);
          if (isNumeric) {
            const u = byId.get(Number(val));
            return u?.phone ? { value: u.id, label: u.phone } : null;
          }
          const u = byPhone.get(str);
          return u
            ? { value: u.id, label: u.phone }
            : str
            ? { value: str, label: str }
            : null; // fallback string
        })
        .filter(Boolean);
    },
    [users]
  );

  const toDeviceOptions = useCallback(
    (devs = []) => {
      const byId = new Map((devices || []).map((d) => [d.value, d]));
      return devs.map((devId) => {
        const id = devId?.device_id || devId?.id || devId;
        return (
          byId.get(id) || {
            value: id,
            label: devId?.device?.name
              ? `${devId.device.name} (${devId.device.serial_number})`
              : String(id),
          }
        );
      });
    },
    [devices]
  );

  // ---------- Estado ----------
  const [formData, setFormData] = useState(() => {
    const rt = initialData?.config?.frequency?.realtime || {};
    return {
      name: initialData?.name || "",
      type: "umbral",
      parameter: initialData?.config?.parameter
        ? {
            label: initialData.config.parameter,
            value: initialData.config.parameter,
          }
        : "",
      active: initialData?.active === 1 || initialData?.active === true,
      notifyEvery:
        typeof rt?.rateLimit === "object"
          ? rt?.rateLimit?.value
          : rt?.rateLimit || "3h",
      period: initialData?.config?.period || "monthly",
      devices: toDeviceOptions(initialData?.config?.devices || []),
      comparator: initialData?.config?.comparator || ">",
      value: initialData?.config?.value || "",
      consecutive: initialData?.config?.consecutive || 1,
      emails: toEmailOptions(rt?.emails || []),
      phones: toPhoneOptions(rt?.phones || []),
    };
  });

  const [errors, setErrors] = useState({});
  const [isValid, setIsValid] = useState(false);

  // ---------- Rehidratación segura ----------
  const lastInitKeyRef = useRef(null);
  const [isDirty, setIsDirty] = useState(false);

  const initKey = useMemo(
    () =>
      initialData?.id ??
      initialData?.uuid ??
      initialData?.name ??
      JSON.stringify({
        name: initialData?.name,
        active: initialData?.active,
        parameter: initialData?.config?.parameter,
        comparator: initialData?.config?.comparator,
        value: initialData?.config?.value,
        consecutive: initialData?.config?.consecutive,
        period: initialData?.config?.period,
        devices: (initialData?.config?.devices || []).map(
          (d) => d?.device_id ?? d?.id ?? d
        ),
        rateLimit: initialData?.config?.frequency?.realtime?.rateLimit,
        emails: initialData?.config?.frequency?.realtime?.emails,
        phones: initialData?.config?.frequency?.realtime?.phones,
      }),
    [initialData]
  );

  const buildStateFromInitial = useCallback(() => {
    if (!initialData) return null;
    const rt = initialData?.config?.frequency?.realtime || {};
    return {
      name: initialData?.name || "",
      type: "umbral",
      parameter: initialData?.config?.parameter
        ? {
            label: initialData.config.parameter,
            value: initialData.config.parameter,
          }
        : "",
      active: initialData?.active === 1 || initialData?.active === true,
      notifyEvery:
        typeof rt?.rateLimit === "object"
          ? rt?.rateLimit?.value
          : rt?.rateLimit || "3h",
      period: initialData?.config?.period || "monthly",
      devices: toDeviceOptions(initialData?.config?.devices || []),
      comparator: initialData?.config?.comparator || ">",
      value: initialData?.config?.value || "",
      consecutive: initialData?.config?.consecutive || 1,
      emails: toEmailOptions(rt?.emails || []),
      phones: toPhoneOptions(rt?.phones || []),
    };
  }, [initialData, toDeviceOptions, toEmailOptions, toPhoneOptions]);

  useEffect(() => {
    if (!initialData || !users?.length || !devices?.length) return;

    const sameInit = lastInitKeyRef.current === initKey;
    if (isDirty && sameInit) return;

    const next = buildStateFromInitial();
    if (!next) return;

    setFormData((prev) => {
      const simplify = (s) => ({
        name: s.name,
        active: !!s.active,
        notifyEvery: s.notifyEvery,
        period: s.period,
        parameter: s.parameter?.label || "",
        comparator: s.comparator,
        value: String(s.value),
        consecutive: Number(s.consecutive),
        deviceIds: (s.devices || []).map((d) => d.value).sort(),
        emailVals: (s.emails || []).map((e) => e.value).sort(),
        phoneVals: (s.phones || []).map((p) => p.value).sort(),
      });
      return JSON.stringify(simplify(prev)) === JSON.stringify(simplify(next))
        ? prev
        : next;
    });

    lastInitKeyRef.current = initKey;
    setIsDirty(false);
  }, [initKey, initialData, users, devices, buildStateFromInitial, isDirty]);

  // ---------- Filtrado de usuarios por dispositivo/privilegios ----------
  const filteredUsers = useFilteredUsersByDevice(
    formData.devices,
    users,
    userPrivilege
  );

  // ---------- Opciones para selects (derivadas de filteredUsers, NO de users) ----------
  const emailOptions = useMemo(
    () =>
      (filteredUsers || [])
        .filter((u) => !!u.email)
        .map((u) => ({ value: u.id, label: u.email })),
    [filteredUsers]
  );
  const phoneOptions = useMemo(
    () =>
      (filteredUsers || [])
        .filter((u) => !!u.phone)
        .map((u) => ({ value: u.id, label: u.phone })),
    [filteredUsers]
  );

  // ---------- Parámetros disponibles (intersección por label) ----------
  const availableParams = useMemo(() => {
    if (!formData.devices?.length) return [];
    const extractParams = (device) => {
      const params = [];
      if (Array.isArray(device.data?.basic_parameters)) {
        device.data.basic_parameters.forEach((param) => {
          if (param.tag && param.visible === 1) {
            params.push({
              value: `basic-${param.idbasic_parameters}`,
              label: param.tag,
              type: "basic",
              id: param.idbasic_parameters,
              unit: param?.units || "",
            });
          }
        });
      }
      if (Array.isArray(device.data?.calculated_parameters)) {
        device.data.calculated_parameters.forEach((param) => {
          if (param.tag && param.device_calculated_parameter?.visible === 1) {
            params.push({
              value: `calc-${param.idcalculated_parameter}`,
              label: param.tag,
              type: "calculated",
              id: param.idcalculated_parameter,
              unit: param?.units || "",
            });
          }
        });
      }
      return params;
    };

    const selectedFull = formData.devices
      .map((sel) => devices.find((d) => d.value === sel.value))
      .filter(Boolean);

    if (!selectedFull.length) return [];

    const perDevice = selectedFull.map(extractParams);
    return perDevice.reduce((acc, curr) =>
      acc.filter((a) => curr.some((b) => b.label === a.label))
    );
  }, [formData.devices, devices]);

  // Si el parámetro seleccionado ya no existe, limpiarlo
  useEffect(() => {
    if (!formData.parameter) return;
    const ok = availableParams.some(
      (p) => p.label === formData.parameter.label
    );
    if (!ok) setFormData((prev) => ({ ...prev, parameter: "" }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableParams]);

  // ---------- Validación ----------
  const validateForm = useCallback(() => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "El nombre es obligatorio";
    if (!formData.devices?.length)
      newErrors.devices = "Selecciona al menos un dispositivo";
    if (!formData.parameter) newErrors.parameter = "Selecciona un parámetro";
    if (!formData.comparator) newErrors.comparator = "Selecciona un comparador";
    if (!formData.value || Number(formData.value) <= 0)
      newErrors.value = "Debes ingresar un valor mayor a 0";
    if (!formData.consecutive || Number(formData.consecutive) < 1)
      newErrors.consecutive = "Debes ingresar un número de mediciones válido";
    setErrors(newErrors);
    setIsValid(Object.keys(newErrors).length === 0);
  }, [formData]);

  useEffect(() => {
    validateForm();
  }, [formData, validateForm]);

  // ---------- Handlers ----------
  const handleChange = (field, value) => {
    setIsDirty(true);

    if (field === "notifyEvery") {
      const normalized =
        typeof value === "object" && value !== null ? value.value : value;
      setFormData((prev) => ({ ...prev, notifyEvery: normalized }));
      return;
    }

    if (field === "devices") {
      // Al cambiar los dispositivos, vaciar destinatarios y (opcional) parámetro si no aplica
      setFormData((prev) => ({
        ...prev,
        devices: value,
        emails: [],
        phones: [],
      }));
      return;
    }

    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // ---------- Submit (solo edición) ----------
  const handleSubmit = () => {
    Swal.fire({
      title: "¿Desea guardar los cambios de la alerta?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: intl.formatMessage({
        id: "confirm.yes",
        defaultMessage: "Sí, continuar.",
      }),
      cancelButtonText: intl.formatMessage({
        id: "confirm.no",
        defaultMessage: "Cancelar",
      }),
    }).then((result) => {
      if (result.isConfirmed && isValid) {
        const payload = {
          type: "UMBRAL",
          name: formData.name?.toUpperCase(),
          active: formData.active ? 1 : 0,
          config: {
            devices: formData.devices.map((d) => d.value),
            parameter: formData.parameter?.label,
            comparator: formData.comparator,
            value: formData.value,
            consecutive: formData.consecutive,
            period: formData.period,
            frequency: {
              realtime: {
                enabled:
                  (formData.emails?.length || 0) > 0 ||
                  (formData.phones?.length || 0) > 0,
                rateLimit: formData.notifyEvery,
                emails: formData.emails.map((e) => e.value), // puede ser ID o email-string (fallback)
                phones: formData.phones.map((p) => p.value), // puede ser ID o phone-string (fallback)
              },
              daily: { enabled: false, time: null, emails: [], phones: [] },
              weekly: {
                enabled: false,
                day: null,
                time: null,
                emails: [],
                phones: [],
              },
              monthly: {
                enabled: false,
                day: null,
                time: null,
                emails: [],
                phones: [],
              },
            },
          },
        };
        onSave(payload);
      } else if (!isValid) {
        Toast.fire({
          icon: "info",
          title: intl.formatMessage(
            {
              id: "error.saveThreshold",
              defaultMessage: "No se pudo guardar la alerta: {errors}",
            },
            { errors: Object.values(errors).join(", ") }
          ),
        });
      }
    });
  };

  // ---------- Render ----------
  const selectedParam =
    formData.parameter &&
    (availableParams.find((p) => p.label === formData.parameter.label) ||
      formData.parameter);

  return (
    <div className="umbral-form">
      <h3 className="umbral-title">Editar alerta: Umbral</h3>

      {/* Nombre */}
      <label className="umbral-label">Nombre de la alerta</label>
      <input
        type="text"
        className="umbral-input"
        value={formData.name}
        onChange={(e) => handleChange("name", e.target.value)}
        placeholder="Ej: Alerta CO2"
      />
      {errors.name && <small className="error-text">{errors.name}</small>}

      {/* Dispositivos */}
      <label className="umbral-label">Aplica a</label>
      <Select
        isMulti
        className="umbral-select"
        options={devices}
        value={formData.devices}
        onChange={(val) => handleChange("devices", val)}
        placeholder="Selecciona uno o más dispositivos"
      />
      {errors.devices && <small className="error-text">{errors.devices}</small>}

      {/* Condición */}
      <label className="umbral-label">Condición</label>
      <div className="umbral-inline">
        <Select
          className="umbral-select"
          options={availableParams}
          value={selectedParam || ""}
          onChange={(val) => handleChange("parameter", val)}
          placeholder="Selecciona parámetro/s"
          isDisabled={!formData.devices.length}
        />
        {errors.parameter && (
          <small className="error-text">{errors.parameter}</small>
        )}

        <select
          className="umbral-select"
          value={formData.comparator}
          onChange={(e) => handleChange("comparator", e.target.value)}
        >
          <option value=">">&gt; Mayor que</option>
          <option value="<">&lt; Menor que</option>
          <option value="=">= Igual a</option>
        </select>
        {errors.comparator && (
          <small className="error-text">{errors.comparator}</small>
        )}

        <input
          type="number"
          className="umbral-input small"
          value={formData.value}
          onChange={(e) => handleChange("value", e.target.value)}
          placeholder="Valor"
        />
        {errors.value && <small className="error-text">{errors.value}</small>}
      </div>

      <div className="umbral-inline">
        durante
        <input
          type="number"
          min="1"
          className="umbral-input tiny"
          value={formData.consecutive}
          onChange={(e) => handleChange("consecutive", e.target.value)}
        />
        medición/es consecutiva/s
      </div>
      {errors.consecutive && (
        <small className="error-text">{errors.consecutive}</small>
      )}

      {/* Activar */}
      <label className="umbral-label">¿Activar?</label>
      <div className="umbral-inline">
        <label>
          <input
            type="radio"
            checked={!!formData.active}
            onChange={() => handleChange("active", true)}
          />{" "}
          Sí
        </label>
        <label>
          <input
            type="radio"
            checked={!formData.active}
            onChange={() => handleChange("active", false)}
          />{" "}
          No
        </label>
      </div>

      {/* Avisos */}
      <label className="umbral-label">Avisos</label>
      <div className="umbral-inline">
        Avísame máximo 1 vez cada
        <select
          className="umbral-select"
          value={formData.notifyEvery}
          onChange={(e) => handleChange("notifyEvery", e.target.value)}
        >
          <option value="1h">1 hora</option>
          <option value="3h">3 horas</option>
          <option value="12h">12 horas</option>
          <option value="24h">24 horas</option>
          <option value="1w">1 semana</option>
        </select>
      </div>

      <div className="umbral-inline">
        <label>
          <input
            type="checkbox"
            checked={formData.emails.length > 0}
            readOnly
          />{" "}
          Enviar aviso por correo electrónico
        </label>
      </div>
      <Select
        isMulti
        className="umbral-select"
        options={emailOptions}
        value={formData.emails}
        onChange={(val) => handleChange("emails", val)}
        placeholder="Selecciona usuarios"
      />

      {/* <div className="umbral-inline">
        <label>
          <input
            type="checkbox"
            checked={formData.phones.length > 0}
            readOnly
          />{" "}
          Enviar un aviso por SMS
        </label>
      </div> */}
      {/* <Select
        isMulti
        className="umbral-select"
        options={phoneOptions}
        value={formData.phones}
        onChange={(val) => handleChange("phones", val)}
        placeholder="Selecciona usuarios"
      /> */}

      {/* Botones */}
      <div className="umbral-actions">
        <button
          className="umbral-btn-save"
          onClick={handleSubmit}
          disabled={!isValid}
        >
          Guardar
        </button>
        <button className="umbral-btn-cancel" onClick={onCancel}>
          Cancelar
        </button>
      </div>
    </div>
  );
};

export default ThresholdAlertFormEdit;
